import React from 'react';
import { Edit2, Users, Briefcase, Calendar, BarChart3, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/utils/cn';
import { usePermission } from '@/core/permissions/usePermission';
import type { TeamDetailData } from '../types/teamsDetail.types';

export function GeneralInfoCard({ data }: { data: TeamDetailData }) {
  const { can } = usePermission();
  const canUpdate = can('teams' as any, 'update' as any);

  const fields = [
    { label: 'Tên team', value: data.name },
    { label: 'Mã team', value: data.code },
    { label: 'Phòng ban', value: 'Thi công' },
    { label: 'Trưởng team', value: data.leader?.full_name, isUser: true, avatar: data.leader?.avatar_url },
    { label: 'Mô tả', value: data.description, fullWidth: true },
    { label: 'Ngày thành lập', value: new Date(data.founded_date).toLocaleDateString('vi-VN') },
    { label: 'Loại team', value: data.team_type },
    { label: 'Khu vực phụ trách', value: data.zone_name },
    { label: 'Dự án phụ trách', value: `${data.project_count} dự án` },
    { label: 'Trạng thái', value: data.status_code === 'active' ? 'Hoạt động' : 'Tạm ngưng', isStatus: true },
  ];

  return (
    <div className="bg-white rounded-md border border-slate-100 p-6 relative">
      <div className="flex items-center justify-between mb-6">
        <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Thông tin chung</h3>
        {canUpdate && (
          <Button variant="outline" className="h-8 sm:text-sm text-xs px-3">
            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
            Chỉnh sửa
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-y-4 gap-x-8">
        {fields.map((f, i) => (
          <div key={i} className={cn("flex flex-col gap-1", f.fullWidth && "col-span-2")}>
            <span className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest">{f.label}</span>
            <div className="flex items-center gap-2">
              {f.isUser && f.avatar && (
                <div className="w-5 h-5 rounded-full overflow-hidden border border-slate-100">
                  <img src={f.avatar} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              {f.isStatus && <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1" />}
              <span className="sm:text-sm text-xs text-slate-700 font-normal">
                {f.value || '---'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectListCard({ projects }: { projects: TeamDetailData['projects'] }) {
  return (
    <div className="bg-white rounded-md border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-50">
        <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Dự án phụ trách ({projects.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50">
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">#</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Dự án</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Vai trò</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Khu vực</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Ngày bắt đầu</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Trạng thái</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {projects.map((p, i) => (
              <tr key={p.id} className="hover:bg-slate-50/30 transition-colors">
                <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">{(i + 1).toString().padStart(2, '0')}</td>
                <td className="px-6 py-4 sm:text-sm text-xs text-slate-700 font-normal">{p.name}</td>
                <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">{p.role}</td>
                <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">{p.zone}</td>
                <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">{p.start_date}</td>
                <td className="px-6 py-4">
                  <StatusBadge label={p.status_label} variant={p.status_variant} className="sm:text-sm text-xs px-2 py-0.5" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="w-full py-3 sm:text-sm text-xs text-blue-600 font-normal hover:bg-slate-50 transition-colors border-t border-slate-50">
        Xem tất cả dự án
      </button>
    </div>
  );
}

export function QuickStatsCard({ data }: { data: TeamDetailData }) {
  const stats = [
    { label: 'Thành viên', value: data.member_count, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Công việc đang làm', value: data.active_work_orders, icon: Briefcase, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Dự án phụ trách', value: data.project_count, icon: Calendar, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Hiệu suất TB', value: `${data.performance_avg}%`, icon: BarChart3, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="bg-white rounded-md border border-slate-100 p-6">
      <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider mb-6">Thống kê nhanh</h3>
      <div className="grid grid-cols-2 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="p-3 rounded-md border border-slate-50 bg-slate-50/20 flex flex-col items-center text-center">
            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center mb-3", s.bg, s.color)}>
              <s.icon className="w-4 h-4" />
            </div>
            <span className="text-xl font-normal text-slate-800">{s.value}</span>
            <span className="sm:text-sm text-xs text-slate-400 mt-1">{s.label}</span>
            <button className="sm:text-sm text-xs text-blue-600 font-normal mt-2 hover:underline">Xem chi tiết</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TopMembersCard({ members }: { members: TeamDetailData['members'] }) {
  return (
    <div className="bg-white rounded-md border border-slate-100 p-6">
      <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider mb-6">Thành viên nổi bật</h3>
      <div className="space-y-4">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 overflow-hidden">
                {m.user.avatar_url ? <img src={m.user.avatar_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center sm:text-sm text-xs text-slate-400">{m.user.full_name[0]}</div>}
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-700 font-normal leading-tight">{m.user.full_name}</p>
                <span className="sm:text-sm text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded-full">{m.user.role_name}</span>
              </div>
            </div>
            <span className="sm:text-sm text-xs text-slate-500">Đóng góp: <span className="font-normal text-slate-700">{m.contribution_percent}%</span></span>
          </div>
        ))}
      </div>
      <button className="w-full mt-6 py-2.5 sm:text-sm text-xs text-slate-500 font-normal hover:bg-slate-50 transition-colors border border-slate-100 rounded-md">
        Xem tất cả thành viên
      </button>
    </div>
  );
}

export function RecentActivityCard({ activities }: { activities: TeamDetailData['activities'] }) {
  return (
    <div className="bg-white rounded-md border border-slate-100 p-6">
      <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider mb-6">Hoạt động gần đây</h3>
      <div className="space-y-6 relative before:absolute before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-slate-100">
        {activities.map((a) => (
          <div key={a.id} className="flex gap-4 relative z-10">
            <div className="w-8 h-8 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-400 overflow-hidden shrink-0">
              {a.user.avatar_url ? <img src={a.user.avatar_url} alt="" className="w-full h-full object-cover" /> : <Clock className="w-4 h-4" />}
            </div>
            <div className="flex-1">
              <p className="sm:text-sm text-xs text-slate-600 leading-normal">
                <span className="font-normal text-slate-800">{a.user.full_name}</span> {a.action}
              </p>
              <span className="sm:text-sm text-xs text-slate-400 mt-1 block">{a.timestamp}</span>
            </div>
          </div>
        ))}
      </div>
      <button className="w-full mt-6 py-2.5 sm:text-sm text-xs text-slate-500 font-normal hover:bg-slate-50 transition-colors border border-slate-100 rounded-md">
        Xem tất cả lịch sử hoạt động
      </button>
    </div>
  );
}
